/**
 * Event configuration — the first Stage A admin screen.
 *
 * Dates, timezone, medical provider and the default dwell. Per-checkpoint
 * cut-offs are not here: they need real Coast-to-Coast distances, which is G4
 * and still deferred.
 *
 * The form validates through `validateEvent` in `packages/domain`, the same
 * function `tools/bootstrap` uses. One definition of a valid event, so the
 * screen and the tool cannot drift apart.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  DWELL_PRESET_MINUTES,
  errors,
  fromDateTimeInput,
  groupTimezones,
  isStorable,
  timezoneOptions,
  toDateTimeInput,
  validateEvent,
  warnings,
  type CairnEvent,
} from '@cairn/domain';

import { updateEvent, watchEvent } from './data/events.js';
import type { Membership } from './data/members.js';
import { ProblemList } from './ProblemList.js';

type SaveState = { readonly kind: 'idle' } | { readonly kind: 'saving' } | { readonly kind: 'saved' } | { readonly kind: 'failed'; readonly message: string };

export function EventSettings({ membership }: { readonly membership: Membership }): React.JSX.Element {
  const [loaded, setLoaded] = useState<CairnEvent | null | undefined>(undefined);
  const [draft, setDraft] = useState<CairnEvent | null>(null);
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });
  const [changedElsewhere, setChangedElsewhere] = useState(false);

  // Refs, not state: the snapshot callback is created once and would otherwise
  // close over the first render's values forever.
  const editing = useRef(false);
  const lastFromServer = useRef<string | null>(null);

  // Only an organiser may write this; the rules say so and the UI should not
  // pretend otherwise by offering inputs that will be refused.
  const canEdit = membership.role === 'organiser';

  // Around 420 options. Rebuilding them on every keystroke in the name field
  // would be wasteful, and design rule 8 targets a five-year-old Android.
  // Above the early returns, so the hook order never depends on load state.
  const timezoneGroups = useMemo(
    () => groupTimezones(timezoneOptions(draft?.timezone ?? '')),
    [draft?.timezone],
  );

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    // Every await in an effect is wrapped. An unhandled rejection in here is
    // invisible — no console entry, nothing on screen, just a page that never
    // finishes loading. That cost an afternoon in step 06.
    void (async () => {
      try {
        const stop = await watchEvent(membership.eventId, (event) => {
          if (cancelled) return;
          setLoaded(event);

          // Follow the document until the organiser starts editing, then stop.
          //
          // Seeding only once looks like the way to avoid destroying what
          // someone is part-way through typing, and it is wrong. onSnapshot
          // fires first from the local cache and again from the server, so
          // seeding once pins the form to the cached copy — which may be
          // minutes or days old. Saving from there writes stale values back
          // and silently reverts whoever changed them.
          const signature = event === null ? null : signatureOf(event);

          if (!editing.current) {
            setDraft(event);
            setChangedElsewhere(false);
          } else if (lastFromServer.current !== null && signature !== lastFromServer.current) {
            // Mid-edit and it moved underneath. Say so rather than choosing
            // for them: their typing is not thrown away, and a blind save is
            // no longer silent.
            setChangedElsewhere(true);
          }

          lastFromServer.current = signature;
        });
        if (cancelled) stop();
        else unsubscribe = stop;
      } catch (error: unknown) {
        if (!cancelled) {
          setSave({ kind: 'failed', message: messageOf(error) });
          setLoaded(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [membership.eventId]);

  const onSave = useCallback(() => {
    if (draft === null) return;
    setSave({ kind: 'saving' });

    void (async () => {
      try {
        const { id: _id, ...edits } = draft;
        await updateEvent(membership.eventId, edits);
        // Start following the document again: what is on screen is now what is
        // stored, so there is nothing left to protect from being overwritten.
        editing.current = false;
        setChangedElsewhere(false);
        setSave({ kind: 'saved' });
      } catch (error: unknown) {
        setSave({ kind: 'failed', message: messageOf(error) });
      }
    })();
  }, [draft, membership.eventId]);

  if (loaded === undefined) return <p>Loading the event…</p>;

  if (loaded === null || draft === null) {
    return (
      <section>
        <h2>Event settings</h2>
        <p role="alert">
          This event could not be read. If it has just been created, reload; otherwise ask an
          organiser whether you are still on it.
        </p>
      </section>
    );
  }

  const problems = validateEvent(draft);
  const blocking = errors(problems);
  const advisory = warnings(problems);

  const set = <K extends keyof CairnEvent>(key: K, value: CairnEvent[K]): void => {
    editing.current = true;
    setDraft({ ...draft, [key]: value });
    setSave({ kind: 'idle' });
  };

  const discardAndFollow = (): void => {
    editing.current = false;
    setChangedElsewhere(false);
    setDraft(loaded);
    setSave({ kind: 'idle' });
  };

  return (
    <section>
      <h2>Event settings</h2>

      {!canEdit && (
        <p>
          You are signed in as {membership.role}. These are read-only — an organiser changes them.
        </p>
      )}

      <label>
        Name
        <input
          type="text"
          value={draft.name}
          disabled={!canEdit}
          onChange={(e) => { set('name', e.target.value); }}
        />
      </label>

      {/*
        A picker, not free text. A mistyped zone is not a visible mistake — it
        renders every time in the wrong place and nothing looks wrong — so the
        cheapest fix is to make it unspellable. The list comes from the runtime
        rather than a hand-kept set, and an unrecognised stored value stays in
        it so it can be corrected deliberately instead of vanishing.

        The guard in packages/domain stays regardless: tools/bootstrap still
        takes a zone as free text, and no render path may throw on one.
      */}
      <label>
        Timezone
        <select
          value={draft.timezone}
          disabled={!canEdit}
          onChange={(e) => { set('timezone', e.target.value); }}
        >
          {timezoneGroups.map((group) => (
            <optgroup key={group.region} label={group.region}>
              {group.zones.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>

      {/*
        Rule 4: times are stored UTC and rendered in the event's timezone, not
        the browser's. The conversion lives in packages/domain so that a phone
        carried across a border does not quietly shift what it shows.
      */}
      <label>
        Starts ({draft.timezone})
        <input
          type="datetime-local"
          value={toDateTimeInput(draft.startsAt, draft.timezone)}
          disabled={!canEdit}
          onChange={(e) => { set('startsAt', fromDateTimeInput(e.target.value, draft.timezone)); }}
        />
      </label>

      <label>
        Ends ({draft.timezone})
        <input
          type="datetime-local"
          value={toDateTimeInput(draft.endsAt, draft.timezone)}
          disabled={!canEdit}
          onChange={(e) => { set('endsAt', fromDateTimeInput(e.target.value, draft.timezone)); }}
        />
      </label>

      <label>
        Default maximum dwell
        <select
          value={String(draft.defaultMaxDwellMinutes)}
          disabled={!canEdit}
          onChange={(e) => { set('defaultMaxDwellMinutes', Number(e.target.value)); }}
        >
          {DWELL_PRESET_MINUTES.map((minutes) => (
            <option key={minutes} value={minutes}>
              {describeMinutes(minutes)}
            </option>
          ))}
        </select>
      </label>
      <p>
        Checkpoints that set their own limit ignore this. Sleep stops must —
        an alert that fires on someone who is simply asleep teaches crews to ignore every alert.
      </p>

      <label>
        Medical provider
        <input
          type="text"
          value={draft.medicalProvider ?? ''}
          disabled={!canEdit}
          onChange={(e) => { set('medicalProvider', e.target.value === '' ? null : e.target.value); }}
        />
      </label>

      {changedElsewhere && (
        <p role="alert">
          Someone else changed this event while you were editing. Saving will overwrite their
          changes.{' '}
          <button type="button" onClick={discardAndFollow}>
            Discard mine and show theirs
          </button>
        </p>
      )}

      <ProblemList problems={blocking} heading="Fix before saving" role="alert" />
      <ProblemList problems={advisory} heading="Worth checking" />

      {canEdit && (
        <p>
          <button type="button" onClick={onSave} disabled={!isStorable(problems) || save.kind === 'saving'}>
            {save.kind === 'saving' ? 'Saving…' : 'Save'}
          </button>
          {save.kind === 'saved' && <span> Saved.</span>}
          {save.kind === 'failed' && <span role="alert"> {save.message}</span>}
        </p>
      )}
    </section>
  );
}


/** "40 minutes", "8 hours" — the vocabulary Reception's presets came in. */
function describeMinutes(minutes: number): string {
  if (minutes < 60) return `${String(minutes)} minutes`;
  const hours = minutes / 60;
  return hours === 1 ? '1 hour' : `${String(hours)} hours`;
}

/**
 * Enough of an event to tell whether the stored copy actually moved. Compared
 * rather than deep-equalled because Dates and object identity both change on
 * every snapshot even when nothing did.
 */
function signatureOf(event: CairnEvent): string {
  return [
    event.name,
    event.timezone,
    event.startsAt.getTime(),
    event.endsAt.getTime(),
    event.defaultMaxDwellMinutes,
    event.medicalProvider ?? '',
  ].join('\u0000');
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
